import React, { useMemo, useState } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { AlertTriangle, CheckSquare, MessageSquare, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export function TaskBulkActionsDialog({
  isBulkActionsOpen, setIsBulkActionsOpen,
  selectedTasks,
  tasks = [], currentUserId,
  sharedTasks = [],
  pendingBulkUpdates, setPendingBulkUpdates,
  categories, teamMembers,
  bulkUpdateTasksMutation, bulkDeleteTasksMutation, deleteSelectedTasks, toast,
  bulkFriends = [], bulkAssistants = [],
}: any) {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const notOwnedCount = useMemo(() => {
    const ownedIds = new Set(tasks.filter((task: any) => task.userId === currentUserId).map((task: any) => task.id));
    return Array.from(selectedTasks as Set<string>).filter(id => !ownedIds.has(id)).length;
  }, [tasks, currentUserId, selectedTasks]);
  const viewOnlyCount = useMemo(() => {
    if (!sharedTasks || !selectedTasks) return 0;
    const viewOnlyIds = new Set(
      sharedTasks
        .filter((st: any) => st.permission !== "edit")
        .map((st: any) => st.id)
    );
    return Array.from(selectedTasks as Set<string>).filter((id) => viewOnlyIds.has(id)).length;
  }, [sharedTasks, selectedTasks]);

  return (
      <Dialog open={isBulkActionsOpen} onOpenChange={(open) => {
        if (!open && (isDeleteConfirmationOpen || bulkDeleteTasksMutation?.isPending)) return;
        if (!open) {
          setPendingBulkUpdates({});
          setIsDeleteConfirmationOpen(false);
        }
        setIsBulkActionsOpen(open);
      }}>
        <DialogContent
          className="!block w-screen h-[100dvh] m-0 rounded-none !overflow-hidden"
          data-testid="dialog-bulk-actions"
          onInteractOutside={(event) => {
            if (isDeleteConfirmationOpen || bulkDeleteTasksMutation?.isPending) event.preventDefault();
          }}
        >
          <div className="flex flex-col h-full">
            <DialogHeader className="flex-shrink-0 pb-4">
              <DialogTitle>Bulk Actions</DialogTitle>
              <DialogDescription>
                Apply changes to {selectedTasks.size} selected task{selectedTasks.size > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            {viewOnlyCount > 0 && (
              <div
                className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-300"
                data-testid="banner-view-only-warning"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>{viewOnlyCount} task{viewOnlyCount > 1 ? "s" : ""}</strong> in your selection{" "}
                  {viewOnlyCount > 1 ? "are" : "is"} view-only. Save will be rejected until you deselect{" "}
                  {viewOnlyCount > 1 ? "them" : "it"} — you need edit access to update view-only tasks.
                </span>
              </div>
            )}
            {notOwnedCount > 0 && (
              <p className="text-sm text-muted-foreground" data-testid="bulk-delete-ownership-warning">
                {notOwnedCount} selected task{notOwnedCount === 1 ? "" : "s"} cannot be deleted here. Deselect tasks you do not own to use bulk delete.
              </p>
            )}
            <div className="space-y-4 py-4 flex-1 overflow-y-auto pb-28">
              <div className="space-y-2">
              <label className="text-sm font-medium">Change Category</label>
              <Select
                value={pendingBulkUpdates.category || ""}
                onValueChange={(value) => {
                  setPendingBulkUpdates((prev: any) => ({ ...prev, category: value }));
                }}
              >
                <SelectTrigger data-testid="select-bulk-category">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  <CategorySelectGroups
                    categories={categories.filter((cat: any) => cat.name !== "Uncategorized")}
                    getTestId={(cat) => `option-category-${cat.name!.toLowerCase()}`}
                  />
                  <SelectItem value="Uncategorized" data-testid="option-category-uncategorized">Uncategorized</SelectItem>
                </SelectContent>
              </Select>
              {pendingBulkUpdates.category && (
                <p className="text-xs text-muted-foreground">Will change to: {pendingBulkUpdates.category}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Change Due Date</label>
              <Input
                type="date"
                value={pendingBulkUpdates.dueDate || ""}
                data-testid="input-bulk-due-date"
                onChange={(e) => {
                  setPendingBulkUpdates((prev: any) => ({ ...prev, dueDate: e.target.value }));
                }}
              />
              {pendingBulkUpdates.dueDate && (
                <p className="text-xs text-muted-foreground">Will change to: {pendingBulkUpdates.dueDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Change Priority</label>
              <Select
                value={pendingBulkUpdates.priority || ""}
                onValueChange={(value) => {
                  setPendingBulkUpdates((prev: any) => ({ ...prev, priority: value }));
                }}
              >
                <SelectTrigger data-testid="select-bulk-priority">
                  <SelectValue placeholder="Select priority..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              {pendingBulkUpdates.priority && (
                <p className="text-xs text-muted-foreground">Will change to: {pendingBulkUpdates.priority}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Assign Tags (comma-separated)</label>
              <Input
                type="text"
                placeholder="tag1, tag2, tag3"
                data-testid="input-bulk-tags"
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                  setPendingBulkUpdates((prev: any) => ({ ...prev, tags: tags.length > 0 ? tags : undefined }));
                }}
              />
              {pendingBulkUpdates.tags && pendingBulkUpdates.tags.length > 0 && (
                <p className="text-xs text-muted-foreground">Will assign tags: {pendingBulkUpdates.tags.join(', ')}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To</label>
              <Select
                value={pendingBulkUpdates.assignedTo || ""}
                onValueChange={(value) => {
                  if (value === "Me") {
                    setPendingBulkUpdates((prev: any) => ({ ...prev, assignedTo: "Me", assignedToOther: undefined }));
                  } else if (value === "Others") {
                    setPendingBulkUpdates((prev: any) => ({ ...prev, assignedTo: "Others" }));
                  } else {
                    setPendingBulkUpdates((prev: any) => ({ ...prev, assignedTo: undefined, assignedToOther: undefined }));
                  }
                }}
              >
                <SelectTrigger data-testid="select-bulk-assigned-to">
                  <SelectValue placeholder="Select assignment..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Me">Assign to Me</SelectItem>
                  <SelectItem value="Others">Assign to Someone Else</SelectItem>
                </SelectContent>
              </Select>
              {pendingBulkUpdates.assignedTo === "Me" && (
                <p className="text-xs text-muted-foreground">Will assign to: Me</p>
              )}
            </div>

            {pendingBulkUpdates.assignedTo === "Others" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Person</label>
                {(bulkFriends.length > 0 || bulkAssistants.length > 0) ? (
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {bulkAssistants.map((assistant: any) => {
                      const name = assistant.user.firstName && assistant.user.lastName 
                        ? `${assistant.user.firstName} ${assistant.user.lastName}`
                        : assistant.user.username || assistant.user.email || "Unknown";
                      const isSelected = pendingBulkUpdates.assignedToOther === name;
                      return (
                        <div
                          key={assistant.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            setPendingBulkUpdates((prev: any) => ({ ...prev, assignedToOther: name, assignedToUserId: assistant.userId }));
                          }}
                          data-testid={`bulk-select-assistant-${assistant.userId}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-medium">
                            {(assistant.user.firstName?.[0] || assistant.user.email?.[0] || "?").toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{name}</div>
                            <div className="text-xs text-muted-foreground">
                              {assistant.assistantType === "virtual_assistant" ? "Virtual Assistant" : "Assistant"}
                            </div>
                          </div>
                          {isSelected && <CheckSquare className="w-4 h-4 text-primary" />}
                        </div>
                      );
                    })}
                    
                    {bulkFriends.map((friendship: any) => {
                      const friend = friendship.friend;
                      const name = friend.firstName && friend.lastName 
                        ? `${friend.firstName} ${friend.lastName}`
                        : friend.username || friend.email || "Unknown";
                      const isSelected = pendingBulkUpdates.assignedToOther === name;
                      return (
                        <div
                          key={friendship.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            setPendingBulkUpdates((prev: any) => ({ ...prev, assignedToOther: name, assignedToUserId: friend.id }));
                          }}
                          data-testid={`bulk-select-friend-${friend.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                            {(friend.firstName?.[0] || friend.email?.[0] || "?").toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{name}</div>
                            <div className="text-xs text-muted-foreground">Friend</div>
                          </div>
                          {isSelected && <CheckSquare className="w-4 h-4 text-primary" />}
                        </div>
                      );
                    })}
                    
                    {bulkAssistants.length === 0 && bulkFriends.length === 0 && (
                      <div className="text-sm text-muted-foreground text-center py-2">
                        No assistants or friends available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground p-3 border rounded-lg text-center">
                    No friends or assistants to assign tasks to. Add friends from the Community section first.
                  </div>
                )}
                {pendingBulkUpdates.assignedToOther && (
                  <p className="text-xs text-muted-foreground">Will assign to: {pendingBulkUpdates.assignedToOther}</p>
                )}
              </div>
            )}

            {/* Show summary of pending changes */}
            {Object.keys(pendingBulkUpdates).filter(k => pendingBulkUpdates[k as keyof typeof pendingBulkUpdates]).length > 0 && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium mb-1">Pending changes:</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside">
                  {pendingBulkUpdates.category && <li>Category: {pendingBulkUpdates.category}</li>}
                  {pendingBulkUpdates.dueDate && <li>Due Date: {pendingBulkUpdates.dueDate}</li>}
                  {pendingBulkUpdates.priority && <li>Priority: {pendingBulkUpdates.priority}</li>}
                  {pendingBulkUpdates.tags && pendingBulkUpdates.tags.length > 0 && <li>Tags: {pendingBulkUpdates.tags.join(', ')}</li>}
                  {pendingBulkUpdates.assignedTo === "Me" && <li>Assigned To: Me</li>}
                  {pendingBulkUpdates.assignedTo === "Others" && pendingBulkUpdates.assignedToOther && <li>Assigned To: {pendingBulkUpdates.assignedToOther}</li>}
                </ul>
              </div>
            )}
            </div>
            {/* Fixed footer for mobile visibility */}
            <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 z-50 safe-area-inset-bottom">
              <DialogFooter className="gap-2">
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteConfirmationOpen(true)}
                  disabled={selectedTasks.size === 0 || notOwnedCount > 0 || bulkDeleteTasksMutation?.isPending || bulkUpdateTasksMutation.isPending}
                  data-testid="button-bulk-delete-tasks"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {bulkDeleteTasksMutation?.isPending ? "Deleting..." : `Delete ${selectedTasks.size} Task${selectedTasks.size === 1 ? "" : "s"}`}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setPendingBulkUpdates({});
                    setIsBulkActionsOpen(false);
                  }}
                  disabled={bulkDeleteTasksMutation?.isPending}
                  data-testid="button-close-bulk-actions"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    console.log('🔄 Bulk Update - pendingBulkUpdates:', pendingBulkUpdates);
                    console.log('🔄 Bulk Update - selectedTasks:', Array.from(selectedTasks));
                    
                    const updates: any = {};
                    if (pendingBulkUpdates.category) updates.category = pendingBulkUpdates.category;
                    if (pendingBulkUpdates.dueDate) updates.dueDate = pendingBulkUpdates.dueDate;
                    if (pendingBulkUpdates.priority) updates.priority = pendingBulkUpdates.priority;
                    if (pendingBulkUpdates.tags && pendingBulkUpdates.tags.length > 0) updates.tags = pendingBulkUpdates.tags;
                    if (pendingBulkUpdates.assignedTo) {
                      updates.assignedTo = pendingBulkUpdates.assignedTo;
                      if (pendingBulkUpdates.assignedTo === "Others" && pendingBulkUpdates.assignedToOther) {
                        updates.assignedToOther = pendingBulkUpdates.assignedToOther;
                        // Include the userId for cross-tenant task assignment
                        if (pendingBulkUpdates.assignedToUserId) {
                          updates.assignedToUserId = pendingBulkUpdates.assignedToUserId;
                        }
                      } else if (pendingBulkUpdates.assignedTo === "Me") {
                        updates.assignedToOther = null; // Clear the other person's name when assigning to self
                        updates.assignedToUserId = null; // Clear the userId when assigning to self
                      }
                    }
                    
                    console.log('🔄 Bulk Update - Constructed updates:', updates);
                    
                    if (Object.keys(updates).length > 0) {
                      bulkUpdateTasksMutation.mutate({
                        taskIds: Array.from(selectedTasks),
                        updates
                      });
                      setPendingBulkUpdates({});
                    } else {
                      toast({
                        title: "No changes",
                        description: "Please select at least one field to update.",
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={bulkDeleteTasksMutation?.isPending || bulkUpdateTasksMutation.isPending || Object.keys(pendingBulkUpdates).filter(k => pendingBulkUpdates[k as keyof typeof pendingBulkUpdates]).length === 0}
                  data-testid="button-save-bulk-actions"
                >
                  {bulkUpdateTasksMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </div>
            <AlertDialog open={isDeleteConfirmationOpen} onOpenChange={setIsDeleteConfirmationOpen}>
              <AlertDialogContent data-testid="dialog-confirm-bulk-delete-tasks">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selectedTasks.size} selected task{selectedTasks.size === 1 ? "" : "s"}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes all selected tasks and their subtasks, or none if any task cannot be deleted. This cannot be undone. Any changes entered above will not be saved.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-cancel-bulk-delete-tasks">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="button-confirm-bulk-delete-tasks"
                    disabled={bulkDeleteTasksMutation?.isPending || notOwnedCount > 0}
                    onClick={() => {
                      setIsDeleteConfirmationOpen(false);
                      deleteSelectedTasks(Array.from(selectedTasks));
                    }}
                  >
                    Delete {selectedTasks.size} Task{selectedTasks.size === 1 ? "" : "s"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogContent>
      </Dialog>
  );
}
